import { useEffect, useState } from "react";
import { apiRequest } from "../../network/apiClient";
import { API_ENDPOINTS } from "../../api/apiEndpoints";

interface ReferrerItem {
  id: number;
  name: string;
  lead_source: string;
  mobile_no: string;
  email: string;
  created_by: string;
  created_at: string;
}

interface ReferrerInventoryResponse {
  data: ReferrerItem[];
  total_pages: number;
  total_rows: number;
  absolute_total: number;
}

interface ReferrerInventoryResult {
  data: ReferrerItem[];
  totalPages: number;
  totalReferrers: number;
  absoluteTotal: number;
  isLoading: boolean;
  refetch: () => void;
}

export const useGetReferrer = (
  page: number,
  pageSize: number,
  searchName: string,
  sortField: string,
  sortOrder: "asc" | "desc"
): ReferrerInventoryResult => {
  const [data, setData] = useState<ReferrerItem[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalReferrers, setTotalReferrers] = useState<number>(0);
  const [absoluteTotal, setAbsoluteTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const buildQueryParams = (): URLSearchParams => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (searchName) {
      queryParams.append("search", searchName);
    }

    if (sortField) {
      queryParams.append("sort_by", sortField);
      queryParams.append("sort_order", sortOrder || "desc");
    }

    return queryParams;
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const queryParams = buildQueryParams();
      const result = await apiRequest(
        `${API_ENDPOINTS.SETTINGS.REFERRER_TABLE}?${queryParams.toString()}`,
        "GET"
      ) as ReferrerInventoryResponse;

      if (result) {
        setData(result.data);
        setTotalPages(result.total_pages);
        setTotalReferrers(result.total_rows);
        setAbsoluteTotal(result.absolute_total);
      } else {
        console.error("Failed to fetch referrer inventory");
      }
    } catch (error) {
      console.error("Error fetching referrer inventory:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, searchName, sortField, sortOrder]);

  const refetch = () => {
    fetchData();
  };

  return { data, totalPages, totalReferrers, absoluteTotal, isLoading, refetch };
};